import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonDataGrid,
  OktonDataGridColumn,
  OktonPageHeader,
  OktonMetricCard,
  useOktonAuth,
  OktonErrorState,
  OktonSelect,
  formatDateTimeSeconds as formatDate,
} from "@okton/platform-ui";

interface PlatformUser {
  subjectId: string;
  name?: string;
  email?: string;
  companyId?: string;
  companyName?: string;
  branchId?: string;
  branchName?: string;
  lastSeenAt?: string;
  sessionActive: boolean;
  ssoAdminGrant: boolean | null;
}

interface UserAccessApplication {
  id: string;
  name: string;
  defaultAllowed: boolean | null;
  grant: boolean | null;
}

export const UsersPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [users, setUsers] = React.useState<PlatformUser[]>([]);
  const [selectedUser, setSelectedUser] = React.useState<PlatformUser | null>(
    null,
  );
  const [accesses, setAccesses] = React.useState<UserAccessApplication[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingAccess, setLoadingAccess] = React.useState(false);
  const [savingSystem, setSavingSystem] = React.useState("");
  const [error, setError] = React.useState("");
  const modal = useDisclosure();

  const loadUsers = React.useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/users?limit=200`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível carregar os usuários.",
        );
      setUsers(payload.data || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar usuários.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  async function openAccess(user: PlatformUser): Promise<void> {
    if (!accessToken) return;
    setSelectedUser(user);
    setAccesses([]);
    setLoadingAccess(true);
    setError("");
    modal.onOpen();
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/users/${encodeURIComponent(user.subjectId)}/access`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível carregar os acessos.",
        );
      setAccesses(payload.data?.applications || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao carregar acessos.",
      );
    } finally {
      setLoadingAccess(false);
    }
  }

  async function changeAccess(systemId: string, mode: string): Promise<void> {
    if (!accessToken || !selectedUser) return;
    setSavingSystem(systemId);
    setError("");
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/users/${encodeURIComponent(selectedUser.subjectId)}/access/${encodeURIComponent(systemId)}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ mode }),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível atualizar o acesso.",
        );
      setAccesses(payload.data?.applications || []);
      await loadUsers();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao atualizar acesso.",
      );
    } finally {
      setSavingSystem("");
    }
  }

  const activeCount = users.filter((item) => item.sessionActive).length;

  const userColumns: OktonDataGridColumn<PlatformUser>[] = [
    {
      key: "user",
      header: "Usuário",
      render: (user) => (
        <Box>
          <Text fontWeight="600">{user.name || user.email || "Usuário"}</Text>
          <Text fontSize="xs" color="gray.500">
            {user.email || user.subjectId}
          </Text>
        </Box>
      ),
    },
    {
      key: "company",
      header: "Empresa / Filial",
      render: (user) => (
        <Box>
          <Text fontSize="sm">
            {user.companyId || "-"} - {user.companyName || "Sem empresa"}
          </Text>
          <Text fontSize="xs" color="gray.500">
            Filial {user.branchId || "-"} - {user.branchName || "Sem filial"}
          </Text>
        </Box>
      ),
    },
    {
      key: "lastSeen",
      header: "Último acesso",
      render: (user) => (
        <Text whiteSpace="nowrap">{formatDate(user.lastSeenAt)}</Text>
      ),
    },
    {
      key: "session",
      header: "Sessão",
      render: (user) => (
        <Badge colorScheme={user.sessionActive ? "green" : "gray"}>
          {user.sessionActive ? "Ativa" : "Sem sessão"}
        </Badge>
      ),
    },
    {
      key: "ssoAdmin",
      header: "Acesso SSO Admin",
      render: (user) => (
        <Badge
          colorScheme={
            user.ssoAdminGrant === false
              ? "red"
              : user.ssoAdminGrant === true
                ? "green"
                : "blue"
          }
        >
          {user.ssoAdminGrant === false
            ? "Bloqueado"
            : user.ssoAdminGrant === true
              ? "Liberado"
              : "Por empresa/filial"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (user) => (
        <Button size="xs" colorScheme="blue" onClick={() => openAccess(user)}>
          Acessos
        </Button>
      ),
    },
  ];

  const accessColumns: OktonDataGridColumn<UserAccessApplication>[] = [
    {
      key: "system",
      header: "Sistema",
      render: (app) => (
        <Box>
          <Text fontWeight="600">{app.name}</Text>
          <Text fontSize="xs" color="gray.500">
            {app.id}
          </Text>
        </Box>
      ),
    },
    {
      key: "default",
      header: "Regra padrão",
      render: (app) => (
        <Badge
          colorScheme={
            app.defaultAllowed === false
              ? "red"
              : app.defaultAllowed === true
                ? "green"
                : "gray"
          }
        >
          {app.defaultAllowed === false
            ? "Restrito"
            : app.defaultAllowed === true
              ? "Liberado"
              : "Sem política"}
        </Badge>
      ),
    },
    {
      key: "override",
      header: "Override do usuário",
      render: (app) => {
        const mode =
          app.grant === null ? "inherit" : app.grant ? "allow" : "deny";
        return (
          <OktonSelect
            name={"user-access-" + app.id}
            size="sm"
            value={mode}
            isDisabled={savingSystem === app.id}
            onChange={(e) => changeAccess(app.id, e.target.value)}
          >
            <option value="inherit">Herdar regra</option>
            <option value="allow">Liberar</option>
            <option value="deny">Bloquear</option>
          </OktonSelect>
        );
      },
    },
  ];

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Usuários"
        subtitle="Usuários conhecidos pela plataforma e seus acessos atuais."
      />

      <SimpleGrid columns={[1, 2]} spacing="4" mb="6">
        <OktonMetricCard label="Usuários conhecidos" value={users.length} />
        <OktonMetricCard label="Com sessão ativa" value={activeCount} />
      </SimpleGrid>

      {error ? (
        <OktonErrorState
          title="Não foi possível carregar os usuários"
          description={error}
          minH="180px"
        />
      ) : (
        <OktonDataGrid
          rows={users}
          columns={userColumns}
          getRowId={(user) => user.subjectId}
          loading={loading}
          emptyMessage="Nenhum usuário conhecido. Os usuários conhecidos pela plataforma aparecerão aqui."
          hidePagination
        />
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Acessos de {selectedUser?.name || selectedUser?.email || "usuário"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {error && (
              <Text color="red.500" mb="4">
                {error}
              </Text>
            )}
            <OktonDataGrid
              rows={accesses}
              columns={accessColumns}
              getRowId={(app) => app.id}
              loading={loadingAccess}
              emptyMessage="Nenhum sistema disponível para este usuário."
              hidePagination
            />
          </ModalBody>
          <ModalFooter>
            <Button onClick={modal.onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
